import React, { useCallback } from "react";
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { getComparator, stableSort } from "../../../_utils/muiTableUtils";
import { useMemo } from "react";
import { Collapse } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createContext } from "react";
import { actionScripts } from "../../../_helpers/actionScripts";
import CmtModal_CmtListAdvFilters from "./CmtModal_CmtListAdvFilters";
import CmtModal_CmtListReports from "./CmtModal_CmtListReports";

export const ChildIssueListContext = createContext();
export const IssueListAdvancedFiltersContext = createContext();
export const IssueListReportsContext = createContext();

export default function CmtListTable(props) {
  const {
    divisionId,
    columns,
    data,
    filters,
    isExpandable,
    expandedDetailComponent,
    showSelectCheckbox,
    showAddButton,
    showAddModal,
    onDelete,
  } = props;

  const [modalState_advFilters, setModalState_advFilters] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_advFilters = () =>
    setModalState_advFilters({
      ...modalState_advFilters,
      showModal: !modalState_advFilters.showModal,
    });

  const [modalState_reports, _setModalState_reports] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_reports = () => _setModalState_reports(!modalState_reports);

  const [searchString, setSearchString] = useState("");
  const [selectedCmtCategory, selectCmtCategory] = useState(null); // filter
  const [selectedStageOwners, selectStageOwners] = useState([]); // filter
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(
    () => setPage(0),
    [order, orderBy, searchString, selectedCmtCategory, selectedStageOwners]
  );

  const searchStringMatchExists = useCallback(
    (rowData) => {
      if (!(searchString || "").trim()) return true;
      const _searchString = searchString.trim().toLowerCase();
      const issueNoMatches = rowData.issueNo.toLowerCase().includes(_searchString);
      const subjectMatches = rowData.subject.toLowerCase().includes(_searchString);
      const stageTitleMatches = columns.some((c) => c.id === "currentStageSnippets")
        ? (rowData.currentStageSnippets || []).some((ss) =>
            (ss.title || "").toLowerCase().includes(_searchString)
          )
        : false;

      let categoryMatches = false;
      const catCol = columns.find((c) => c.id === "category");
      if (catCol) {
        categoryMatches = catCol
          .searchData(rowData)
          .toLowerCase()
          .includes(_searchString);
      }

      let stageMatches = false;
      const stageCol = columns.find((c) => c.id === "currentStageSnippets");
      if (stageCol) {
        stageMatches = (stageCol.searchData(rowData) || []).some((stageTitle) =>
          stageTitle.toLowerCase().includes(_searchString)
        );
      }

      let ownerMatches = false;
      const ownerCol = columns.find((c) => c.id === "currentStageOwnerSnippets");
      if (ownerCol) {
        ownerMatches = (ownerCol.searchData(rowData) || []).some((ownerName) =>
          ownerName.toLowerCase().includes(_searchString)
        );
      }

      return (
        issueNoMatches ||
        subjectMatches ||
        stageTitleMatches ||
        categoryMatches ||
        stageMatches ||
        ownerMatches
      );
    },
    [searchString]
  );

  const filteredRowCount = useRef(0);
  const rows = useMemo(() => {
    const filteredBySearchString = (data || [])
      .map((d) => ({ ...d }))
      .filter((d) => searchStringMatchExists(d));
    const filteredByCmtCategory = selectedCmtCategory
      ? filteredBySearchString.filter((d) =>
          (d.cmtCategoryIds || []).includes(selectedCmtCategory.value)
        )
      : filteredBySearchString;
    const filteredByStageOwners =
      (selectedStageOwners || []).length > 0
        ? filteredByCmtCategory.filter((d) =>
            selectedStageOwners.some(
              (sso) =>
                (d.currentStageOwnerSnippets || [])
                  .filter((sos) => sos.ownerId)
                  .map((sos) => sos.ownerId)
                  .includes(sso.value) ||
                (d.primaryOwnerId &&
                  selectedStageOwners.some((sso) => sso.value === d.primaryOwnerId))
            )
          )
        : filteredByCmtCategory;
    filteredRowCount.current = (filteredByStageOwners || []).length;
    return stableSort(filteredByStageOwners, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [
    data,
    order,
    orderBy,
    page,
    rowsPerPage,
    searchString,
    selectedCmtCategory,
    selectedStageOwners,
  ]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick_selectAll = (e) => {
    if (e.target.checked) {
      const newSelected = rows.map((n) => n.issueId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChange_search = (e) => setSearchString(e.target.value);

  const handleClick_details = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedIssue = data.find((d) => d.issueId === id);
    if (selectedIssue)
      actionScripts.OPEN_QMS_CMT.run(
        divisionId,
        selectedIssue.issueId,
        selectedIssue.issueNo
      );
  };

  const selectItem = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (data || []).length) : 0;

  const handleClick_filter = (filter) => (e) => {
    e.preventDefault();
    if (filter.id === "advFilters")
      setModalState_advFilters({
        showModal: true,
        data: {
          ...(modalState_advFilters.data || {}),
          isForPendingList: filter.isForPendingList,
        },
      });
    if (filter.id === "reports")
      _setModalState_reports({
        showModal: true,
        data: {
          ...(modalState_reports.data || {}),
          isForPendingList: filter.isForPendingList,
        },
      });
  };

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex flex-row flex-wrap w-100 mb-2">
        <div className="qms-filter-component qms-filter-component__input flex-grow-1 mr-2">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text bg-lightgray">
                <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
              </span>
            </div>
            <input
              name="filter"
              id="filter"
              type="search"
              className="form-control"
              value={searchString}
              onChange={handleChange_search}
              placeholder="Search (any field)..."
            />
          </div>
        </div>
        {showAddButton && (
          <div className="qms-filter-component qms-filter-component__btn mr-2">
            <button id="add" name="add" className="btn btn-info" onClick={showAddModal}>
              <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
              Add...
            </button>
          </div>
        )}
        {(filters || []).map((f, index) => {
          if (f.id === "cmtCategory")
            return (
              <div
                key={`cmtListFilterKey_${index}`}
                className="qms-filter-component qms-filter-component__select mr-2"
              >
                <CmtCategoryFilter
                  filter={f}
                  selected={selectedCmtCategory}
                  handleChange={selectCmtCategory}
                />
              </div>
            );
          if (f.id === "stageOwner")
            return (
              <div
                key={`stageOwnerFilterKey_${index}`}
                className="qms-filter-component qms-filter-component__select-wide mr-2"
              >
                <StageOwnerFilter
                  filter={f}
                  selected={selectedStageOwners}
                  handleChange={selectStageOwners}
                />
              </div>
            );
          return (
            <div
              key={`cmtListFilterKey_${index}`}
              className="qms-filter-component qms-filter-component__btn mr-2"
            >
              <button
                id={f.id}
                name={f.id}
                className="btn btn-secondary"
                onClick={handleClick_filter(f)}
              >
                <FontAwesomeIcon icon={f.icon} className="fa-fw mr-2" />
                {f.label}
              </button>
            </div>
          );
        })}
        {showSelectCheckbox && selected.length > 0 && (
          <div className="qms-filter-component qms-filter-component__btn">
            <button
              name="delete"
              id="delete"
              onClick={() => {
                onDelete(selected);
                setSelected([]); // Reset selected
              }}
              className="btn btn-danger ml-2"
            >
              <FontAwesomeIcon icon="trash-alt" className="fa-fw mr-2" />
              Delete ({selected.length})
            </button>
          </div>
        )}
      </div>
      <Box sx={{ width: "100%" }}>
        <Paper elevation={0} variant="outlined" sx={{ width: "100%", mb: 2 }}>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={filteredRowCount.current}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              "& .MuiTablePagination-selectLabel": {
                my: "auto",
              },
              "& .MuiTablePagination-displayedRows": {
                my: "auto",
              },
            }}
          />
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={"small"}>
              <CmtListTableHead
                columns={columns}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleClick_selectAll}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                isExpandable={isExpandable}
                showSelectCheckbox={showSelectCheckbox}
              />
              <TableBody>
                {rows.map((row, index) => {
                  const isItemSelected = isSelected(row.issueId);
                  return (
                    <CmtListTableRow
                      key={`qms-data-table-row_${row.issueId}`}
                      row={row}
                      index={index}
                      isItemSelected={isItemSelected}
                      selectItem={selectItem}
                      columns={columns}
                      handleClick_details={handleClick_details}
                      isExpandable={isExpandable}
                      expandedDetailComponent={expandedDetailComponent}
                      showSelectCheckbox={showSelectCheckbox}
                    />
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 22 * emptyRows }}>
                    <TableCell colSpan={(columns || []).length + 3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      {modalState_advFilters.showModal && (
        <CmtModal_CmtListAdvFilters
          modalState={modalState_advFilters}
          toggleModal={toggleModal_advFilters}
        />
      )}
      {modalState_reports.showModal && (
        <CmtModal_CmtListReports
          modalState={modalState_reports}
          toggleModal={toggleModal_reports}
        />
      )}
    </div>
  );
}

function CmtListTableHead(props) {
  const {
    columns,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    isExpandable,
    showSelectCheckbox,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {isExpandable && (
          <TableCell sx={{ width: "3%", minWidth: "10px", paddingX: 0.5 }} />
        )}
        {showSelectCheckbox && (
          <TableCell sx={{ width: "3%", minWidth: "10px", paddingX: 0 }}>
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all",
              }}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.numeric ? "right" : "left"}
            // padding={column.disablePadding ? 'none' : "normal"}
            sortDirection={orderBy === column.id ? order : false}
            sx={{
              fontWeight: "fontWeightBold",
              width: column.width || "",
              minWidth: column.minWidth || "100px",
              paddingX: 0.5,
            }}
          >
            <TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : "asc"}
              onClick={createSortHandler(column.id)}
            >
              {column.label}
              {orderBy === column.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    </TableHead>
  );
}

CmtListTableHead.propTypes = {
  columns: PropTypes.array.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  isExpandable: PropTypes.bool.isRequired,
  showSelectCheckbox: PropTypes.bool.isRequired,
};

function CmtListTableRow(props) {
  const {
    row,
    index: rowIndex,
    isItemSelected,
    selectItem,
    columns,
    handleClick_details,
    isExpandable,
    expandedDetailComponent,
    showSelectCheckbox,
  } = props;

  const [isOpen, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!isOpen);

  const labelId = `qms-data-table-checkbox-${rowIndex}`;

  const handleChange_check = (id) => (e) => selectItem(id);

  const handleClick_row = (id) => (e) => {
    e.preventDefault();
    selectItem(id);
  };

  const handleClick_open = (e) => {
    e.stopPropagation();
    toggleOpen();
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleClick_row(row.issueId)}
        tabIndex={-1}
        selected={isOpen}
        sx={{ "& > *": { borderBottom: "unset" } }}
      >
        {isExpandable && (
          <TableCell sx={{ width: "3%", minWidth: "10px", paddingX: 0.5 }}>
            <IconButton aria-label="expand row" size="small" onClick={handleClick_open}>
              {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          </TableCell>
        )}
        {showSelectCheckbox && (
          <TableCell sx={{ width: "3%", minWidth: "10px", paddingX: 0 }}>
            <Checkbox
              color="primary"
              checked={isItemSelected}
              onChange={handleChange_check(row.issueId)}
              inputProps={{
                "aria-labelledby": labelId,
              }}
            />
          </TableCell>
        )}
        {(columns || []).map((column, colIndex) => {
          const key = `cmt-list-table-cell-${rowIndex}-${colIndex}`;
          if (colIndex === 0)
            return (
              <TableCell
                key={key}
                component="th"
                id={labelId}
                scope="row"
                align={column.numeric ? "right" : "left"}
                sx={{ paddingX: 0.5 }}
              >
                {column.render ? column.render(row) : row[column.id]}
              </TableCell>
            );
          else
            return (
              <TableCell
                key={key}
                align={column.numeric ? "right" : "left"}
                sx={{ paddingX: 0.5 }}
              >
                {column.render ? column.render(row) : row[column.id]}
              </TableCell>
            );
        })}
        <TableCell
          key={`qms-data-table-cell-${rowIndex}-edit`}
          align="right"
          sx={{ width: "10%", minWidth: "100px", paddingX: 0.5 }}
        >
          <button
            id="test"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClick_details(row.issueId)}
          >
            <FontAwesomeIcon icon="edit" className="fa-fw mr-2" />
            View...
          </button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            borderBottom: isOpen ? 1 : "none",
            backgroundColor: "#eeeeee",
          }}
          colSpan={(columns || []).length + 3}
        >
          <Collapse in={isOpen} unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <ChildIssueListContext.Provider
                value={{ data: { issueId: row.issueId, fetchListOnly: true } }}
              >
                {expandedDetailComponent}
              </ChildIssueListContext.Provider>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function CmtCategoryFilter(props) {
  const { filter, selected, handleChange } = props;

  return (
    <Select
      id={filter.id}
      name={filter.id}
      options={filter.data.selectOptions}
      value={selected}
      onChange={(selected) => handleChange(selected)}
      isClearable
      isSearchable={false}
      placeholder="by Category"
    />
  );
}

function StageOwnerFilter(props) {
  const { filter, selected, handleChange } = props;

  return (
    <Select
      id={filter.id}
      name={filter.id}
      options={filter.data.selectOptions}
      value={selected}
      onChange={(selected) => handleChange(selected)}
      isClearable
      isMulti
      placeholder="by Owner(s)"
    />
  );
}
